// Image-import (photos → PDF) themes for /apps/pdf-tools/pdf-editor.
//
// Same contract as md-theme.ts / sheet-theme.ts: a plain serializable
// object holding every style the image layout engine (photo-layout.ts)
// understands, grouped the way the import screen presents them — page,
// layout (grid + fit), frame (border/shadow/corner), caption, and the
// running page header/footer.

import type { MdAlign, MdPageStyle } from "./md-theme.ts";
import type { SheetPageTextStyle } from "./sheet-theme.ts";

/** How each image fills its cell. */
export type PhotoFit =
  /** Whole image inside the cell, aspect preserved (letterboxed). */
  | "contain"
  /** Fill the cell, aspect preserved, overflow cropped centre-out. */
  | "cover"
  /** Cell exactly matches the image's aspect (no empty band). */
  | "shrink";

export interface PhotoLayoutStyle {
  /** Cells per row / per column. 1×1 = one image per page. */
  columns: number;
  rows: number;
  fit: PhotoFit;
  /** Gap between cells, pt. */
  gap: number;
  /** Rotate a page to match its single image's orientation. Only
   *  meaningful at 1×1 — a grid keeps the theme's page orientation. */
  autoOrient: boolean;
}

export interface PhotoFrameStyle {
  /** Border thickness, pt; 0 = none. */
  borderWidth: number;
  borderColor: string;
  /** White mat between the border and the image, pt. */
  matWidth: number;
  matColor: string;
  /** Drop shadow offset, pt; 0 = none. */
  shadowOffset: number;
  shadowColor: string;
}

export interface PhotoCaptionStyle {
  /** "" = no captions. */
  source: "" | "filename" | "index";
  family: string;
  size: number;
  color: string;
  italic: boolean;
  align: MdAlign;
  /** Gap between the image and its caption, pt. */
  spaceBefore: number;
}

export interface PhotoThemeConfig {
  page: MdPageStyle;
  layout: PhotoLayoutStyle;
  frame: PhotoFrameStyle;
  caption: PhotoCaptionStyle;
  pageHeader: SheetPageTextStyle;
  pageFooter: SheetPageTextStyle;
}

export interface PhotoThemePreset {
  id: string;
  label: string;
  blurb: string;
  config: PhotoThemeConfig;
}

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

/** The shared skeleton presets are built from — “Full page” verbatim. */
const FULL: PhotoThemeConfig = {
  page: {
    preset: "A4",
    landscape: false,
    marginTop: 36,
    marginRight: 36,
    marginBottom: 36,
    marginLeft: 36,
    background: "#ffffff",
  },
  layout: { columns: 1, rows: 1, fit: "contain", gap: 14, autoOrient: true },
  frame: {
    borderWidth: 0,
    borderColor: "#d0d7de",
    matWidth: 0,
    matColor: "#ffffff",
    shadowOffset: 0,
    shadowColor: "#00000022",
  },
  caption: {
    source: "",
    family: "helvetica",
    size: 8.5,
    color: "#57606a",
    italic: false,
    align: "center",
    spaceBefore: 5,
  },
  pageHeader: { text: "", align: "left", size: 8, color: "#57606a" },
  pageFooter: { text: "", align: "center", size: 8, color: "#57606a" },
};

function themed(patch: {
  page?: Partial<MdPageStyle>;
  layout?: Partial<PhotoLayoutStyle>;
  frame?: Partial<PhotoFrameStyle>;
  caption?: Partial<PhotoCaptionStyle>;
  pageHeader?: Partial<SheetPageTextStyle>;
  pageFooter?: Partial<SheetPageTextStyle>;
}): PhotoThemeConfig {
  const base = clone(FULL);
  return {
    page: { ...base.page, ...patch.page },
    layout: { ...base.layout, ...patch.layout },
    frame: { ...base.frame, ...patch.frame },
    caption: { ...base.caption, ...patch.caption },
    pageHeader: { ...base.pageHeader, ...patch.pageHeader },
    pageFooter: { ...base.pageFooter, ...patch.pageFooter },
  };
}

export const PHOTO_THEMES: readonly PhotoThemePreset[] = [
  {
    id: "full",
    label: "Full page",
    blurb: "One image per page, orientation matched",
    config: clone(FULL),
  },
  {
    id: "edge",
    label: "Edge to edge",
    blurb: "One image per page, no margins, cropped to fill",
    config: themed({
      page: { marginTop: 0, marginRight: 0, marginBottom: 0, marginLeft: 0 },
      layout: { fit: "cover", autoOrient: true },
    }),
  },
  {
    id: "contact",
    label: "Contact sheet",
    blurb: "3 × 4 thumbnail grid with filenames",
    config: themed({
      page: { marginTop: 32, marginRight: 32, marginBottom: 32, marginLeft: 32 },
      layout: { columns: 3, rows: 4, fit: "contain", gap: 10, autoOrient: false },
      caption: { source: "filename", size: 6.5, spaceBefore: 3 },
      pageFooter: { text: "Page {page} of {pages}", align: "center" },
    }),
  },
  {
    id: "album",
    label: "Album",
    blurb: "2 × 2 framed prints on a soft ground",
    config: themed({
      page: {
        marginTop: 40,
        marginRight: 40,
        marginBottom: 40,
        marginLeft: 40,
        background: "#f5f5f4",
      },
      layout: { columns: 2, rows: 2, fit: "shrink", gap: 20, autoOrient: false },
      frame: {
        borderWidth: 0.75,
        borderColor: "#d6d3d1",
        matWidth: 6,
        matColor: "#ffffff",
        shadowOffset: 2,
        shadowColor: "#00000022",
      },
      caption: { source: "filename", size: 7.5, italic: true, spaceBefore: 5 },
    }),
  },
  {
    id: "polaroid",
    label: "Polaroid",
    blurb: "Wide white mat under each image, 2 per page",
    config: themed({
      page: { background: "#e7e5e4" },
      layout: { columns: 1, rows: 2, fit: "shrink", gap: 22, autoOrient: false },
      frame: {
        borderWidth: 0,
        borderColor: "#ffffff",
        matWidth: 12,
        matColor: "#ffffff",
        shadowOffset: 3,
        shadowColor: "#0000001f",
      },
      caption: { source: "filename", size: 9, color: "#44403c", spaceBefore: 6 },
    }),
  },
  {
    id: "proof",
    label: "Proof sheet",
    blurb: "Numbered 2 × 3 grid with header and footer",
    config: themed({
      layout: { columns: 2, rows: 3, fit: "contain", gap: 14, autoOrient: false },
      frame: { borderWidth: 0.5, borderColor: "#d0d7de" },
      caption: { source: "index", size: 7, align: "left", spaceBefore: 4 },
      pageHeader: { text: "{file}", align: "left" },
      pageFooter: { text: "{date} · page {page} of {pages}", align: "right" },
    }),
  },
];

export const clonePhotoTheme = (c: PhotoThemeConfig): PhotoThemeConfig => clone(c);

/** Every family the theme can draw with (face resolution). */
export function photoThemeFamilies(c: PhotoThemeConfig): string[] {
  return [...new Set([c.caption.family])];
}
